import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import Button from "@mui/material/Button";
import { Box, Typography, Divider, Chip } from "@mui/material";
import {
  Badge as BadgeIcon,
  Description as DescriptionIcon,
  CalendarToday as DateIcon,
  Person as PersonIcon,
  LocalHospital as HospitalIcon,
  MedicalServices as MedecinIcon,
  LocalPharmacy as PharmacyIcon,
  Science as LabIcon,
  CheckCircle as ValidIcon,
  Cancel as RejectIcon,
  AccessTime as PendingIcon,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import { API } from "../../../api/API";
import MedicationIcon from "@mui/icons-material/Medication";
const StatusChip = styled(Chip)(({ theme }) => ({
  fontWeight: 600,
  borderRadius: "16px",
  padding: "4px 8px",
}));

const InfoRow = ({
  icon,
  label,
  value,
  color = "",
  iconColor = "text.secondary",
}) => (
  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
    <Box sx={{ mr: 2, color: iconColor }}>{icon}</Box>
    <Box>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body1" color={color}>
        {value || "Non renseigné"}
      </Typography>
    </Box>
  </Box>
);

const formatDate = (dateString) => {
  if (!dateString) return "Non renseigné";
  return new Date(dateString).toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const ViewPrescription = ({ open, onClose, prescription }) => {
  const [prescriptionDetails, setPrescriptionDetails] = useState(null);

  useEffect(() => {
    if (prescription && prescription.id) {
      const fetchDetails = async () => {
        try {
          const response = await API.get(
            `/prescriptions/${prescription.id}/details?request_from=medecin_conseille`
          );
          const { data } = response.data;
          console.log("data", data);
          setPrescriptionDetails(data);
        } catch (error) {
          console.error("Error fetching prescription details:", error);
        }
      };
      fetchDetails();
    }
  }, [prescription]);

  if (!prescription) return null;

  const details = prescriptionDetails || prescription;
  const statusIcons = {
    validée: <ValidIcon color="success" />,
    rejetée: <RejectIcon color="error" />,
    en_attente: <PendingIcon color="warning" />,
  };

  return (
    <Modal
      size="xl"
      show={open}
      onHide={onClose}
      aria-labelledby="contained-modal-title-vcenter"
      centered
      backdrop="static"
      scrollable
      className="mt-4"
    >
      <Modal.Header className="bg-success text-light " closeButton>
        <Typography
          variant="h5"
          component="h2"
          sx={{
            fontWeight: 600,
            textAlign: "center",
            width: "100%",
          }}
        >
          Prescription N°{details.numero}
        </Typography>
      </Modal.Header>

      <Modal.Body>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: 3,
            p: 2,
          }}
        >
          {/* Colonne de gauche - Infos principales */}
          <Box
            sx={{
              flex: 1,
              backgroundColor: "background.paper",
              p: 2,
              borderRadius: 2,
              boxShadow: 1,
            }}
          >
            <Typography
              variant="h6"
              color="success"
              sx={{
                fontWeight: 600,
                mb: 2,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <DescriptionIcon /> Informations principales
            </Typography>

            <InfoRow
              icon={<BadgeIcon color="warning" />}
              label="Numéro"
              value={details.numero}
            />

            <InfoRow
              icon={<DateIcon />}
              label="Date de prescription"
              value={formatDate(details.date_prescription_agent_assurance)}
            />

            <Divider></Divider>
            <InfoRow
              icon={<MedicationIcon color="success" />}
              label={`Prescrit par: ${details.agentclinique?.matricule} `}
              value={details.agentclinique?.full_name}
              iconColor="success"
            />

            <InfoRow
              icon={<PersonIcon color="success" />}
              label={`Assuré : ${details.matricule_assure} `}
              value={details.assure?.fullName}
            />

            <InfoRow
              label="Type"
              value={details.type}
              icon={<DescriptionIcon />}
            />
          </Box>

          {/* Colonne de droite - Détails supplémentaires */}
          <Box
            sx={{
              flex: 2,
              backgroundColor: "background.paper",
              p: 2,
              borderRadius: 2,
              boxShadow: 1,
            }}
          >
            <Typography
              variant="h6"
              color="success"
              sx={{
                fontWeight: 600,
                mb: 2,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <DescriptionIcon /> Détails complémentaires
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Box sx={{ mb: 3 }}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{ mb: 1 }}
              >
                Description
              </Typography>
              <Typography variant="body1">
                {details.description || "Aucune description disponible"}
              </Typography>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{ mb: 1 }}
              >
                Commentaires
              </Typography>
              <Typography>{details.comment_agent_clinique}</Typography>
            </Box>
          </Box>
        </Box>
      </Modal.Body>

      <Modal.Footer>
        <Button
          variant="contained"
          onClick={onClose}
          color="primary"
          sx={{
            px: 4,
            py: 1,
            fontWeight: 600,
            borderRadius: "8px",
          }}
        >
          Fermer
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ViewPrescription;
